import React from "react";
import { useParams, Navigate } from "react-router-dom";
import { Container, Typography, Box, Chip, Avatar } from "@mui/material";
import styled from "styled-components";
import BlogSEO from "../../components/SEO/BlogSEO";
import GrowComponent from "../../components/GrowComponent";
import blogPosts from "./blogContent";

const BlogDetail = () => {
  const { slug } = useParams();
  const post = blogPosts.find(p => p.slug === slug);

  if (!post || !post.published) {
    return <Navigate to="/blog" replace />;
  }

  return (
    <>
      <BlogSEO 
        post={post}
        isDetailPage={true}
      />
      <Wrapper>
        {/* Hero Section */}
        <HeroSection>
          <Container maxWidth="md">
            <Box className="hero-content">
              <Chip 
                label={post.category} 
                className="category-chip"
              />
              <Typography variant="h2" component="h1" className="post-title">
                {post.title}
              </Typography>
              <Typography variant="h6" className="post-excerpt">
                {post.excerpt}
              </Typography>
              
              {/* Author and Meta Information */}
              <Box className="post-meta">
                <Box className="author-info">
                  <Avatar src="/team/author-avatar.jpg" className="author-avatar" />
                  <Box>
                    <Typography variant="body1" className="author-name">
                      {post.author}
                    </Typography>
                    <Box className="meta-details">
                      <Typography variant="body2" className="post-date">
                        {new Date(post.date).toLocaleDateString('en-US', { 
                          year: 'numeric', 
                          month: 'long', 
                          day: 'numeric' 
                        })}
                      </Typography>
                      <span className="meta-separator">•</span>
                      <Typography variant="body2" className="read-time">
                        {post.readTime}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </Box>
            </Box>
          </Container>
        </HeroSection>

        {/* Featured Image */}
        <Container maxWidth="lg" sx={{ mb: 4 }}>
          <FeaturedImage>
            <img src={post.image} alt={post.title} />
          </FeaturedImage>
        </Container>

        {/* Article Content */}
        <Container maxWidth="md" sx={{ mb: 6 }}>
          <ArticleContent>
            <div dangerouslySetInnerHTML={{ __html: post.content }} />
          </ArticleContent>

          {/* Tags */}
          <TagsSection>
            <Typography variant="h6" className="tags-title">
              Tags
            </Typography>
            <Box className="tags-container">
              {post.tags.map((tag, index) => (
                <TagChip key={index}>
                  {tag}
                </TagChip>
              ))}
            </Box>
          </TagsSection>

          {/* Author Bio Section */}
          <AuthorBio>
            <Box className="author-info">
              <Avatar src="/team/author-avatar.jpg" className="author-avatar-large" />
              <Box>
                <Typography variant="h6" className="author-name">
                  About {post.author}
                </Typography>
                <Typography variant="body2" className="author-bio">
                  {post.authorBio}
                </Typography>
              </Box>
            </Box>
          </AuthorBio>
        </Container>

        <GrowComponent />
      </Wrapper>
    </>
  );
};

export default BlogDetail;

const Wrapper = styled.div`
  background: #fff;
`;

const HeroSection = styled.section`
  background: linear-gradient(135deg, #f8f9ff 0%, #eef6ff 100%);
  padding: 4rem 0;
  text-align: center;

  .hero-content {
    max-width: 800px;
    margin: 0 auto;
  }

  .category-chip {
    background-color: #31436D !important;
    color: white !important;
    font-weight: 600 !important;
    margin-bottom: 1.5rem !important;
  }

  .post-title {
    color: #31436D;
    font-weight: 700;
    line-height: 1.2;
    margin-bottom: 1.5rem;
    
    @media (max-width: 768px) {
      font-size: 2rem;
    }
  }

  .post-excerpt {
    color: #31436D;
    font-weight: 400;
    line-height: 1.6;
    opacity: 0.8;
    margin-bottom: 2rem;
  }

  .post-meta {
    display: flex;
    justify-content: center;
    margin-top: 2rem;

    .author-info {
      display: flex;
      align-items: center;
      text-align: left;
    }

    .author-avatar {
      width: 48px !important;
      height: 48px !important;
      margin-right: 1rem !important;
    }

    .author-name {
      color: #31436D;
      font-weight: 600;
      margin-bottom: 0.25rem;
    }

    .meta-details {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      color: #666;
      font-size: 0.875rem;

      .meta-separator {
        margin: 0 0.25rem;
      }
    }
  }
`;

const FeaturedImage = styled.div`
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0px 8px 32px rgba(0, 0, 0, 0.1);

  img {
    width: 100%;
    height: 400px;
    object-fit: cover;
    
    @media (max-width: 768px) {
      height: 250px;
    }
  }
`;

const ArticleContent = styled.div`
  color: #31436D;
  line-height: 1.8;
  font-size: 1.125rem;

  h3 {
    color: #31436D;
    font-weight: 700;
    margin: 2.5rem 0 1rem 0;
    font-size: 1.5rem;
  }

  p {
    margin-bottom: 1.5rem;
  }

  ul, ol {
    margin-bottom: 1.5rem;
    padding-left: 2rem;
  }

  li {
    margin-bottom: 0.5rem;
  }

  strong {
    font-weight: 600;
    color: #2789FD;
  }

  @media (max-width: 768px) {
    font-size: 1rem;
    
    h3 {
      font-size: 1.3rem;
    }
  }
`;

const TagChip = styled.div`
  display: inline-block;
  padding: 6px 12px;
  margin: 4px 8px 4px 0;
  background-color: #f0f8ff;
  color: #2789FD;
  font-size: 0.875rem;
  font-weight: 500;
  border-radius: 16px;
  border: 1px solid #e3f2fd;
`;

const TagsSection = styled.div`
  margin-top: 3rem;
  padding-top: 2rem;
  border-top: 1px solid #e0e0e0;

  .tags-title {
    color: #31436D;
    font-weight: 600;
    margin-bottom: 1rem;
  }

  .tags-container {
    display: flex;
    flex-wrap: wrap;
  }
`;

const AuthorBio = styled.div`
  background: #f8f9ff;
  border-radius: 12px;
  padding: 2rem;
  margin-top: 3rem;
  border: 1px solid #e3f2fd;

  .author-info {
    display: flex;
    align-items: flex-start;
  }

  .author-avatar-large {
    width: 64px !important;
    height: 64px !important;
    margin-right: 1.5rem !important;
  }

  .author-name {
    color: #31436D;
    font-weight: 600;
    margin-bottom: 0.5rem;
  }

  .author-bio {
    color: #666;
    line-height: 1.6;
  }

  @media (max-width: 600px) {
    .author-info {
      flex-direction: column;
      text-align: center;
      
      .author-avatar-large {
        margin: 0 auto 1rem auto !important;
      }
    }
  }
`;
